import {
  FormAsyncAutocomplete,
  Icon,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiDelete, mdiPlus } from "@mdi/js";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { AxiosError } from "axios";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Vendor } from "../../../@types/services/Vendor";
import { FormattedTextField } from "../../../components/atoms/FormattedTextField";
import { TaxNumberField } from "../../../components/atoms/TaxNumberField/TaxNumberField";
import { DeliveryCalendar } from "../../../components/molecules/DeliveryCalendar/DeliveryCalendar";
import { FileUpload } from "../../../components/molecules/FileUpload";
import { useAuth } from "../../../hooks/useAuth";
import {
  createDeliverySchedulingSchema,
  type DeliverySchedulingFormData,
} from "../../../schemas/deliverySchedulingSchema";
import { createDeliverySchedule } from "../../../services/deliverySchedule";
import { validatePurchaseOrder } from "../../../services/purchaseOrders";
import { fetchVendors } from "../../../services/vendors";

enum DeliveryStatus {
  PENDING = "pending",
  APPROVED = "approved",
}

interface DeliveryEvent {
  end: string;
  id: string;
  start: string;
  status: DeliveryStatus;
  title: string;
}

interface DeliveryScheduleData {
  vendor_id?: string;
  purchase_order_number?: string;
  taxnumber1?: string;
  license_plate: string;
  invoice_number: number;
  invoice_series: string;
  invoice_key: string;
  observations: string;
  file_attachments: string[];
  document_reference?: string;
}

const DeliveryScheduling: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [events] = useState<DeliveryEvent[]>([]);
  const [selectedSlotEvent, setSelectedSlotEvent] =
    useState<DeliveryEvent | null>(null);
  const [poValidationStatus, setPoValidationStatus] = useState<
    "idle" | "validating" | "valid" | "invalid"
  >("idle");
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [showAdvancedFields, setShowAdvancedFields] = useState<boolean>(false);
  const [vendorData, setVendorData] = useState<{
    taxnumber1?: string;
    fullvendorname?: string;
  } | null>(null);
  const [cnpjValidationStatus, setCnpjValidationStatus] = useState<
    "idle" | "valid" | "invalid"
  >("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const calendarRef = useRef<{
    refreshCalendarData: () => Promise<void>;
  } | null>(null);

  const deliverySchedulingSchema = createDeliverySchedulingSchema(
    user?.roles_names.includes("Delivery Schedule - Manage Time Slot") ?? false,
  );

  const hasManageTimeSlotPermission = user?.roles_names.includes(
    "Delivery Schedule - Manage Time Slot",
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      deliveryDateTime: null,
      file_attachments: [],
      invoiceKey: "",
      invoiceNumber: undefined,
      invoiceSeries: "",
      licensePlate: "",
      observations: "",
      purchase_order_number: hasManageTimeSlotPermission ? null : "",
      taxnumber1: "",
      ...(hasManageTimeSlotPermission && { document_reference: "" }),
      ...(hasManageTimeSlotPermission && { vendor_id: null }),
      packages: [
        {
          height: "",
          id: crypto.randomUUID(),
          length: "",
          quantity: "",
          volumeWeight: "",
          width: "",
        },
      ],
    },
    mode: "onChange",
    resolver: zodResolver(deliverySchedulingSchema),
    reValidateMode: "onChange",
  });

  const { enqueueSnackbar } = useSnackbar();

  const allEvents = useMemo(() => {
    if (selectedSlotEvent) {
      return [...events, selectedSlotEvent];
    }
    return events;
  }, [events, selectedSlotEvent]);

  const deliveryDateTime = watch("deliveryDateTime");
  const isValidDeliveryDateTime = deliveryDateTime !== null;

  const getFieldColor = (status: string): "success" | "error" | "primary" => {
    if (status === "valid") return "success";
    if (status === "invalid") return "error";
    return "primary";
  };

  const onSubmit = async (data: DeliverySchedulingFormData): Promise<void> => {
    try {
      const extractVendorId = (vendor: Vendor): string => {
        if (typeof vendor === "string") {
          return vendor;
        }
        if (vendor && typeof vendor === "object" && "id" in vendor) {
          return vendor.id.toString();
        }
        return "";
      };

      const deliveryScheduleData: DeliveryScheduleData = {
        file_attachments: data.file_attachments,
        invoice_key: data.invoiceKey,
        invoice_number: data.invoiceNumber,
        invoice_series: data.invoiceSeries,
        license_plate: data.licensePlate.replace(/-/g, ""),
        observations: data.observations,
        purchase_order_number: data.purchase_order_number || "",
        taxnumber1: data.taxnumber1,
        vendor_id: hasManageTimeSlotPermission
          ? extractVendorId(data.vendor_id)
          : user?.vendor?.id?.toString() || "",
        ...(hasManageTimeSlotPermission &&
          data.document_reference && {
            document_reference: data.document_reference || "",
          }),
      };

      await createDeliverySchedule({
        delivery_date_time: data.deliveryDateTime?.toISOString(),
        delivery_schedule: deliveryScheduleData,
        has_manage_time_slot_permission: !!hasManageTimeSlotPermission,
        packages: data.packages.map((pkg) => {
          return {
            height: pkg.height,
            length: pkg.length,
            quantity: pkg.quantity,
            volume_weight: pkg.volumeWeight,
            width: pkg.width,
          };
        }),
      });

      reset();
      setSelectedSlotEvent(null);
      setShowAdvancedFields(false);
      setCnpjValidationStatus("idle");
      setVendorData(null);
      setPoValidationStatus("idle");
      setValidationMessage("");

      enqueueSnackbar(t("deliveryScheduling.successMessage"), {
        variant: "success",
      });

      if (calendarRef.current) {
        setTimeout(() => {
          calendarRef.current?.refreshCalendarData();
        }, 100);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("deliveryScheduling.errorMessage"), {
        variant: "error",
      });
    }
  };

  const handleSlotSelect = (start: Date, end: Date): void => {
    setValue("deliveryDateTime", dayjs(start), {
      shouldValidate: true,
    });

    const newEvent: DeliveryEvent = {
      end: end.toISOString(),
      id: crypto.randomUUID(),
      start: start.toISOString(),
      status: DeliveryStatus.PENDING,
      title: t("deliveryScheduling.newAppointment"),
    };

    setSelectedSlotEvent(newEvent);
  };

  const handleCancel = (): void => {
    setSelectedSlotEvent(null);
    setShowAdvancedFields(false);
    setCnpjValidationStatus("idle");
    setVendorData(null);
    reset();
  };

  const handleExistingAppointmentClick = (): void => {
    setSelectedSlotEvent(null);
    setShowAdvancedFields(false);
    setCnpjValidationStatus("idle");
    setVendorData(null);
    reset();
  };

  const addPackage = (): void => {
    const currentPackages = watch("packages");
    setValue("packages", [
      ...currentPackages,
      {
        height: "",
        id: crypto.randomUUID(),
        length: "",
        quantity: "",
        volumeWeight: "",
        width: "",
      },
    ]);
  };

  const removePackage = (index: number): void => {
    const currentPackages = watch("packages");
    if (currentPackages.length > 1) {
      const newPackages = currentPackages.filter((_, i) => i !== index);
      setValue("packages", newPackages);
    }
  };

  const validatePurchaseOrderLocal = async (
    poNumber: string,
  ): Promise<void> => {
    if (poNumber.length !== 10) return;

    setPoValidationStatus("validating");
    setValidationMessage(
      t("deliveryScheduling.form.purchaseOrder.validation.validating"),
    );
    setShowAdvancedFields(false);

    try {
      const data = await validatePurchaseOrder(poNumber);

      setVendorData(data.vendor);
      setPoValidationStatus("valid");
      setValidationMessage(
        t("deliveryScheduling.form.purchaseOrder.validation.success"),
      );
      setShowAdvancedFields(true);
    } catch (error) {
      console.error(error);
      setPoValidationStatus("invalid");
      setValidationMessage(
        (error as AxiosError<{ message: string }>)?.response?.data?.message ||
          t("deliveryScheduling.form.purchaseOrder.validation.error"),
      );
      setShowAdvancedFields(false);
    }
  };

  const handlePoChange = (newValue: string): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setPoValidationStatus("idle");
    setValidationMessage("");
    setCnpjValidationStatus("idle");
    setVendorData(null);

    if (!hasManageTimeSlotPermission && newValue.length === 10) {
      validatePurchaseOrderLocal(newValue);
    }
  };

  const validateCnpj = (taxNumber: string): void => {
    if (hasManageTimeSlotPermission) {
      setCnpjValidationStatus("idle");
      return;
    }

    if (!vendorData?.taxnumber1) {
      setCnpjValidationStatus("idle");
      return;
    }

    const cleanTaxNumber = taxNumber.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const vendorTaxNumber = vendorData.taxnumber1
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase();

    if (cleanTaxNumber.length > 0) {
      if (cleanTaxNumber === vendorTaxNumber) {
        setCnpjValidationStatus("valid");
      } else {
        setCnpjValidationStatus("invalid");
      }
    } else {
      setCnpjValidationStatus("idle");
    }
  };

  const handleFilesChange = (fileNames: string[]): void => {
    setValue("file_attachments", fileNames);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ boxSizing: "border-box", p: 3, width: "100%" }}>
      <Typography gutterBottom variant="h5">
        {t("deliveryScheduling.title")}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
        <Paper sx={{ p: 2 }}>
          <Typography gutterBottom variant="h6">
            {t("deliveryScheduling.deliveryDateTime")}
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography
              sx={{
                color: "text.secondary",
              }}
              variant="body2"
            >
              {t("deliveryScheduling.scheduleInfo.part1")}
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                mt: 1,
              }}
              variant="body2"
            >
              {t("deliveryScheduling.scheduleInfo.part2")}
            </Typography>
          </Alert>

          <Controller
            control={control}
            name="deliveryDateTime"
            render={() => (
              <Box sx={{ mb: 2 }}>
                {isLoading ? (
                  <Box
                    sx={{
                      alignItems: "center",
                      backgroundColor: "#fafafa",
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      display: "flex",
                      justifyContent: "center",
                      minHeight: 400,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <DeliveryCalendar
                    events={allEvents}
                    onExistingAppointmentClick={handleExistingAppointmentClick}
                    onSlotSelect={handleSlotSelect}
                    ref={calendarRef}
                  />
                )}
                {!!errors.deliveryDateTime && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {typeof errors.deliveryDateTime.message === "string"
                      ? errors.deliveryDateTime.message
                      : "Data e hora de entrega é obrigatória"}
                  </Alert>
                )}
              </Box>
            )}
          />
        </Paper>

        {isValidDeliveryDateTime && (
          <>
            <Paper sx={{ mt: 2, p: 2 }}>
              <Typography gutterBottom sx={{ mb: 2 }} variant="h6">
                {t("deliveryScheduling.deliveryDetails")}
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    lg: "repeat(5, 1fr)",
                    md: "repeat(4, 1fr)",
                    sm: "repeat(2, 1fr)",
                    xs: "1fr",
                  },
                  width: "100%",
                }}
              >
                {!hasManageTimeSlotPermission && (
                  <Controller
                    control={control}
                    name="purchase_order_number"
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error: fieldError },
                    }) => {
                      const editableValue = (value || "").substring(3);

                      return (
                        <Box>
                          <TextField
                            color={getFieldColor(poValidationStatus)}
                            error={
                              !!fieldError || poValidationStatus === "invalid"
                            }
                            fullWidth
                            helperText={
                              fieldError?.message ||
                              validationMessage ||
                              t("deliveryScheduling.form.purchaseOrder.helper")
                            }
                            inputRef={ref}
                            label={t(
                              "deliveryScheduling.form.purchaseOrder.label",
                            )}
                            name="purchase_order_number"
                            onChange={(e) => {
                              const newValue = e.target.value.replace(
                                /\D/g,
                                "",
                              );
                              if (newValue.length <= 7) {
                                const fullValue = `450${newValue}`;
                                onChange(fullValue);
                                handlePoChange(fullValue);
                              }
                            }}
                            placeholder={t(
                              "deliveryScheduling.form.purchaseOrder.placeholder",
                            )}
                            size="small"
                            slotProps={{
                              input: {
                                endAdornment: poValidationStatus ===
                                  "validating" && (
                                  <Box
                                    sx={{
                                      alignItems: "center",
                                      display: "flex",
                                    }}
                                  >
                                    <Typography
                                      color="primary"
                                      variant="caption"
                                    >
                                      {t(
                                        "deliveryScheduling.form.purchaseOrder.validation.processing",
                                      )}
                                    </Typography>
                                  </Box>
                                ),
                                inputProps: {
                                  inputMode: "numeric",
                                  maxLength: 7,
                                  pattern: "[0-9]*",
                                },
                                startAdornment: (
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: "bold",
                                      mr: 0.5,
                                      pointerEvents: "none",
                                      userSelect: "none",
                                    }}
                                  >
                                    450
                                  </Box>
                                ),
                              },
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                            value={editableValue}
                          />
                        </Box>
                      );
                    }}
                  />
                )}
                {hasManageTimeSlotPermission && (
                  <FormAsyncAutocomplete
                    control={control}
                    fetchOptions={fetchVendors}
                    filters={(searchTerm: string) => ({
                      name: searchTerm,
                    })}
                    getOptionLabel={(option: Vendor) =>
                      option ? option?.fullvendorname : ""
                    }
                    isOptionEqualToValue={(option, value) => {
                      const optionId = option?.id?.toString();
                      const valueId = value?.toString();
                      return optionId === valueId;
                    }}
                    label={t("deliveryScheduling.selectVendor")}
                    minSearchLength={3}
                    name="vendor_id"
                    queryKey={["vendors", "list"]}
                  />
                )}
                {hasManageTimeSlotPermission && (
                  <Controller
                    control={control}
                    name="document_reference"
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error: fieldError },
                    }) => (
                      <TextField
                        error={!!fieldError}
                        fullWidth
                        helperText={fieldError?.message}
                        inputRef={ref}
                        label={t(
                          "deliveryScheduling.form.documentReference.label",
                        )}
                        name="document_reference"
                        onChange={onChange}
                        placeholder={t(
                          "deliveryScheduling.form.documentReference.placeholder",
                        )}
                        size="small"
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        value={value || ""}
                      />
                    )}
                  />
                )}
                {hasManageTimeSlotPermission ? (
                  <Controller
                    control={control}
                    name="taxnumber1"
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error },
                    }) => (
                      <TextField
                        color="primary"
                        error={!!error}
                        fullWidth
                        helperText={error?.message}
                        inputRef={ref}
                        label={t("deliveryScheduling.form.taxNumber.label")}
                        name="taxnumber1"
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={t(
                          "deliveryScheduling.form.taxNumber.placeholder",
                        )}
                        size="small"
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        value={value || ""}
                      />
                    )}
                  />
                ) : (
                  <TaxNumberField
                    color={getFieldColor(cnpjValidationStatus)}
                    control={control}
                    disabled={poValidationStatus !== "valid"}
                    documentType="cnpj"
                    error={
                      !!errors.taxnumber1 || cnpjValidationStatus === "invalid"
                    }
                    helperText={
                      errors.taxnumber1?.message ||
                      (cnpjValidationStatus === "valid" &&
                        `${t("deliveryScheduling.form.taxNumber.success")} - ${
                          vendorData?.fullvendorname
                        }`) ||
                      (cnpjValidationStatus === "invalid" &&
                        t(
                          "deliveryScheduling.form.taxNumber.error.doesNotMatch",
                        )) ||
                      (poValidationStatus !== "valid" &&
                        t(
                          "deliveryScheduling.form.taxNumber.validatePOFirst",
                        )) ||
                      t("deliveryScheduling.form.taxNumber.helper")
                    }
                    label={t("deliveryScheduling.form.taxNumber.label")}
                    name="taxnumber1"
                    onValueChange={(values) => {
                      validateCnpj(values.value);
                    }}
                    placeholder={t(
                      "deliveryScheduling.form.taxNumber.placeholder",
                    )}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                )}
                {((showAdvancedFields && cnpjValidationStatus === "valid") ||
                  hasManageTimeSlotPermission) && (
                  <Controller
                    control={control}
                    name="licensePlate"
                    render={({
                      field: { onChange, value, ref },
                      fieldState: { error: fieldError },
                    }) => (
                      <TextField
                        error={!!fieldError}
                        fullWidth
                        helperText={fieldError?.message}
                        inputRef={ref}
                        label={t("deliveryScheduling.form.licensePlate.label")}
                        name="licensePlate"
                        onChange={(e) => {
                          let inputValue = e.target.value.toUpperCase();
                          inputValue = inputValue.replace(/[^A-Z0-9-]/g, "");

                          const cleanValue = inputValue.replace(
                            /[^A-Z0-9]/g,
                            "",
                          );

                          if (cleanValue.length >= 3) {
                            const prefix = cleanValue.slice(0, 3);
                            const suffix = cleanValue.slice(3);

                            if (
                              cleanValue.length === 7 &&
                              /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(cleanValue)
                            ) {
                              inputValue = cleanValue;
                            } else if (suffix.length > 0) {
                              inputValue = `${prefix}-${suffix}`;
                            } else {
                              inputValue = prefix;
                            }
                          }

                          const maxLength = inputValue.includes("-") ? 8 : 7;
                          if (inputValue.length <= maxLength) {
                            onChange(inputValue);
                          }
                        }}
                        placeholder="ABC-1234"
                        size="small"
                        slotProps={{
                          input: {
                            inputProps: {
                              maxLength: 8,
                              style: { textTransform: "uppercase" },
                            },
                          },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        value={value || ""}
                      />
                    )}
                  />
                )}
              </Box>
            </Paper>

            {!!(showAdvancedFields || hasManageTimeSlotPermission) && (
              <Paper sx={{ mt: 2, p: 2 }}>
                <Typography gutterBottom sx={{ mb: 2 }} variant="h6">
                  {t("deliveryScheduling.form.invoiceInformation")}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      md: "1fr 1fr 2fr",
                      sm: "repeat(2, 1fr)",
                      xs: "1fr",
                    },
                    mb: 2,
                  }}
                >
                  <FormattedTextField
                    control={control}
                    fullWidth
                    label={t("deliveryScheduling.form.invoiceNumber.label")}
                    name="invoiceNumber"
                    placeholder={t(
                      "deliveryScheduling.form.invoiceNumber.placeholder",
                    )}
                    size="small"
                    slotProps={{
                      input: {
                        inputProps: {
                          inputMode: "numeric",
                          maxLength: 9,
                          pattern: "[0-9]*",
                        },
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                  <FormattedTextField
                    control={control}
                    fullWidth
                    label={t("deliveryScheduling.form.invoiceSeries.label")}
                    name="invoiceSeries"
                    placeholder={t(
                      "deliveryScheduling.form.invoiceSeries.placeholder",
                    )}
                    size="small"
                    slotProps={{
                      input: {
                        inputProps: {
                          inputMode: "numeric",
                          maxLength: 3,
                          pattern: "[0-9]*",
                        },
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                  />
                  <FormattedTextField
                    control={control}
                    format="#### #### #### #### #### #### #### #### #### #### ####"
                    fullWidth
                    helperText={t("deliveryScheduling.form.invoiceKey.helper")}
                    label={t("deliveryScheduling.form.invoiceKey.label")}
                    name="invoiceKey"
                    placeholder={t(
                      "deliveryScheduling.form.invoiceKey.placeholder",
                    )}
                    size="small"
                    slotProps={{
                      input: {
                        inputProps: {
                          inputMode: "numeric",
                          maxLength: 55,
                        },
                      },
                      inputLabel: {
                        shrink: true,
                      },
                    }}
                    sx={{
                      gridColumn: {
                        md: "3 / -1",
                        sm: "1 / -1",
                        xs: "1 / -1",
                      },
                    }}
                  />
                </Box>
              </Paper>
            )}

            {!!(showAdvancedFields || hasManageTimeSlotPermission) && (
              <Paper sx={{ mt: 2, p: 2 }}>
                <Controller
                  control={control}
                  name="file_attachments"
                  render={() => (
                    <FileUpload
                      allowMultipleFiles
                      onFilesChange={handleFilesChange}
                    />
                  )}
                />
              </Paper>
            )}

            <Paper sx={{ mt: 2, p: 2 }}>
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  {t("deliveryScheduling.packages.title")}
                </Typography>
                <Button
                  onClick={addPackage}
                  size="small"
                  startIcon={<Icon icon={mdiPlus} />}
                  variant="outlined"
                >
                  {t("deliveryScheduling.packages.addPackage")}
                </Button>
              </Box>

              {watch("packages").map((pkg, index) => (
                <Box
                  key={pkg.id}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      lg: "repeat(5, 1fr)",
                      md: "repeat(3, 1fr)",
                      sm: "repeat(2, 1fr)",
                      xs: "1fr",
                    },
                    mb: 2,
                    p: 2,
                    position: "relative",
                    width: "100%",
                  }}
                >
                  {watch("packages").length > 1 && (
                    <IconButton
                      onClick={() => removePackage(index)}
                      size="small"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                      }}
                    >
                      <Icon icon={mdiDelete} />
                    </IconButton>
                  )}

                  <Typography
                    sx={{ gridColumn: "1 / -1", mb: 1 }}
                    variant="subtitle2"
                  >
                    {t("deliveryScheduling.packages.packageNumber", {
                      number: index + 1,
                    })}
                  </Typography>

                  <Controller
                    control={control}
                    name={`packages.${index}.quantity`}
                    render={({ field: { onChange, value, name, ref } }) => (
                      <TextField
                        error={!!errors.packages?.[index]?.quantity}
                        fullWidth
                        helperText={errors.packages?.[index]?.quantity?.message}
                        inputRef={ref}
                        label={t("deliveryScheduling.packages.quantity.label")}
                        name={name}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        type="number"
                        value={value}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`packages.${index}.volumeWeight`}
                    render={({ field: { onChange, value, name, ref } }) => (
                      <TextField
                        error={!!errors.packages?.[index]?.volumeWeight}
                        fullWidth
                        helperText={
                          errors.packages?.[index]?.volumeWeight?.message
                        }
                        inputRef={ref}
                        label={t(
                          "deliveryScheduling.packages.volumeWeight.label",
                        )}
                        name={name}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          input: {
                            inputProps: {
                              step: "0.01",
                            },
                          },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        type="number"
                        value={value}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`packages.${index}.length`}
                    render={({ field: { onChange, value, name, ref } }) => (
                      <TextField
                        error={!!errors.packages?.[index]?.length}
                        fullWidth
                        helperText={errors.packages?.[index]?.length?.message}
                        inputRef={ref}
                        label={t("deliveryScheduling.packages.length.label")}
                        name={name}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          input: {
                            inputProps: {
                              step: "0.01",
                            },
                          },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        type="number"
                        value={value}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`packages.${index}.width`}
                    render={({ field: { onChange, value, name, ref } }) => (
                      <TextField
                        error={!!errors.packages?.[index]?.width}
                        fullWidth
                        helperText={errors.packages?.[index]?.width?.message}
                        inputRef={ref}
                        label={t("deliveryScheduling.packages.width.label")}
                        name={name}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          input: {
                            inputProps: {
                              step: "0.01",
                            },
                          },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        type="number"
                        value={value}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name={`packages.${index}.height`}
                    render={({ field: { onChange, value, name, ref } }) => (
                      <TextField
                        error={!!errors.packages?.[index]?.height}
                        fullWidth
                        helperText={errors.packages?.[index]?.height?.message}
                        inputRef={ref}
                        label={t("deliveryScheduling.packages.height.label")}
                        name={name}
                        onChange={onChange}
                        size="small"
                        slotProps={{
                          input: {
                            inputProps: {
                              step: "0.01",
                            },
                          },
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        type="number"
                        value={value}
                      />
                    )}
                  />
                </Box>
              ))}
            </Paper>

            <Box
              sx={{
                display: "flex",
                flexDirection: {
                  sm: "row",
                  xs: "column",
                },
                gap: 2,
                justifyContent: "flex-end",
                mt: 3,
              }}
            >
              <Button
                color="error"
                onClick={handleCancel}
                sx={{
                  width: {
                    sm: "auto",
                    xs: "100%",
                  },
                }}
                type="button"
                variant="outlined"
              >
                {t("common.cancel")}
              </Button>
              <Button
                color="primary"
                sx={{
                  width: {
                    sm: "auto",
                    xs: "100%",
                  },
                }}
                type="submit"
                variant="contained"
              >
                {t("deliveryScheduling.requestSchedule")}
              </Button>
            </Box>
          </>
        )}
      </form>
    </Box>
  );
};

// biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
export const Route = createFileRoute("/_private/delivery_schedules/new")({
  component: DeliveryScheduling,
  beforeLoad: ({ context }) => {
    const { auth } = context;
    if (!auth.user?.roles_names.includes("Delivery Schedule - Create")) {
      throw redirect({ to: "/forbidden" });
    }
  },
});
